/**
 * Rolling strategy calculation functions for covered call positions.
 *
 * Requirements: 7.5, 8.5, 9.4
 */

/**
 * Calculates the net credit or debit from a roll operation.
 *
 * Formula: Pn - Bb - Cb - Cs
 * Positive result = net credit (roll generates income)
 * Negative result = net debit (roll costs money)
 *
 * @param newPremium - Premium received for the new option (Pn)
 * @param buybackCost - Cost to buy back the current option (Bb)
 * @param buybackCommission - Commission on the buyback transaction (Cb)
 * @param saleCommission - Commission on the new sale transaction (Cs)
 * @returns Net credit (positive) or debit (negative), rounded to 2 decimal places
 */
export function calculateRollNetCreditDebit(
  newPremium: number,
  buybackCost: number,
  buybackCommission: number,
  saleCommission: number
): number {
  const result = newPremium - buybackCost - buybackCommission - saleCommission;
  return Math.round(result * 100) / 100 || 0;
}

/**
 * Calculates cumulative premium income across all options sold against a position.
 *
 * Sum of all premiums received minus sum of all buyback costs.
 * Options that expired worthless have no buyback cost (contributing full premium).
 *
 * @param optionHistory - Array of option history entries with premium and optional buybackCost
 * @returns Cumulative premium income, rounded to 2 decimal places
 */
export function calculateCumulativePremiumIncome(
  optionHistory: Array<{ premium: number; buybackCost?: number }>
): number {
  let totalPremiums = 0;
  let totalBuybacks = 0;

  for (const entry of optionHistory) {
    totalPremiums += entry.premium;
    totalBuybacks += entry.buybackCost ?? 0;
  }

  const result = totalPremiums - totalBuybacks;
  return Math.round(result * 100) / 100;
}

/**
 * Calculates total commissions accumulated across all roll transactions.
 *
 * Sums both buyback commissions and sale commissions from every roll.
 *
 * @param rolls - Array of roll entries with buybackCommission and saleCommission
 * @returns Total roll commissions, rounded to 2 decimal places
 */
export function calculateTotalRollCommissions(
  rolls: Array<{ buybackCommission: number; saleCommission: number }>
): number {
  let total = 0;

  for (const roll of rolls) {
    total += roll.buybackCommission + roll.saleCommission;
  }

  return Math.round(total * 100) / 100;
}
